/**
 * Die Rechnungen des Messkonzept-Editors (`routes/concept.tsx`), ohne React —
 * dort, wo ein Fehler still eine falsche Fassung speichern würde (HM-190).
 *
 * **Eine gewählte Zählwerksrolle gilt nur für den Zähler, für den sie gewählt
 * wurde.** Die Auswahl „Welches Zählwerk zählt …?" erscheint nur, wenn der
 * Zähler mehr als eine zulässige Rolle führt. Wer von einem Zweirichtungszähler
 * (Erzeugung als Einspeisung) auf einen Einrichtungszähler wechselt, sieht sie
 * nicht mehr — und eine mitgeschleppte Rolle `energy_export_total` an einem
 * Zähler ohne dieses Zählwerk wäre eine Erzeugung, die still zur Lücke wird.
 * Deshalb wird jede Rolle gegen den Zähler und den Eingang geprüft, bevor sie
 * in die Zuordnung geht.
 */
import type { ApiComponents } from "@hausmeter/sdk";

type Template = ApiComponents["schemas"]["ConceptTemplate"];
type TemplateInput = Template["inputs"][number];
type Slot = ApiComponents["schemas"]["ConceptTemplateSlot"];
type Meter = ApiComponents["schemas"]["Meter"];
type Layout = ApiComponents["schemas"]["ConceptLayout"];
type Concept = ApiComponents["schemas"]["MeasurementConcept"];

export type Role = Meter["registers"][number]["role"];
export type Source = { readonly meteringPointId: string; readonly role: Role };
/** Die Zuordnung Eingang → Messstelle samt Zählwerksrolle, wie sie im Konzept steckt. */
export type Assignment = Record<string, Source>;

/** Die Zuordnung, die in einer Fassung steckt — aus ihren Zählwerksknoten. */
export function assignmentOf(concept: Pick<Concept, "nodes">): Assignment {
  return Object.fromEntries(
    concept.nodes.flatMap((node) =>
      node.type === "meter_register" && node.meteringPointId !== null && node.registerRole !== null
        ? [[node.key, { meteringPointId: node.meteringPointId, role: node.registerRole }]]
        : [],
    ),
  );
}

/**
 * Die Messstelle, die in einem Steckplatz steckt — `null`, wenn keine. Messkonzept und
 * Erzeugungsanlagen (#42) fragen beide so; zwei Fassungen liefen beim nächsten Steckplatz
 * mit zwei Eingängen auseinander.
 */
export function meteringPointOfSlot(
  template: Pick<Template, "inputs">,
  slot: Pick<Slot, "key">,
  assignment: Assignment,
): string | null {
  const inputs = template.inputs.filter((input) => input.slot === slot.key);
  const ids = new Set(
    inputs.map((input) => assignment[input.key]?.meteringPointId).filter((id) => id !== undefined),
  );
  return ids.size === 1 ? [...ids][0]! : null;
}

/** Was diese Rechnungen von einem Zähler brauchen: seine Zählwerksrollen. */
type RegistersOf = { readonly registers: readonly { readonly role: Role }[] };

/**
 * Ob ein Eingang die Aufnahme eines Erzeugungszählers ist — `isIntakeKey` der Engine (#40, #43),
 * die diese App nicht importiert: `aufnahme` am ersten, `aufnahme.2` … an den weiteren. Der Test
 * hält die Abschrift gegen die Engine.
 */
export function isIntakeInput(key: string): boolean {
  return key === "aufnahme" || /^aufnahme\.\d+$/u.test(key);
}

/**
 * Was sich an der Aufnahme geändert hat, seit die Fassung gespeichert wurde (#42, je
 * Erzeugungszähler seit #43): welche Zähler sie jetzt zählen können, ohne dass die Fassung es tut
 * (`gained`), und welche sie nicht mehr können (`lost`) — beides zugleich ist möglich, und
 * „Übernehmen" (`adoptIntake`) wendet beides an. Genannt werden sie mit „Der Erzeugungszähler",
 * wenn die Fassung nur einen hat, sonst mit ihren Bezeichnungen. `null`, wenn nichts abweicht.
 *
 * `generationSlots` sind die Erzeugungs-Steckplätze der Vorlage: Ob „der" Erzeugungszähler
 * gemeint ist, hängt an ihrer Zahl, nicht daran, wie viele eine Aufnahme zählen könnten.
 */
export interface IntakeChange {
  readonly gained: readonly string[];
  readonly lost: readonly string[];
}

export function intakeChange(
  intakeSlots:
    | readonly {
        readonly slotKey: string;
        readonly counted: boolean;
        readonly available: boolean;
      }[]
    | null
    | undefined,
  generationSlots: readonly Pick<Slot, "key" | "label">[],
): IntakeChange | null {
  // `undefined` von einer API vor #43 — dann gibt es nichts abzugleichen.
  if (intakeSlots === null || intakeSlots === undefined) return null;
  const nameOf = (slotKey: string) =>
    generationSlots.length <= 1
      ? "Der Erzeugungszähler"
      : `„${generationSlots.find((slot) => slot.key === slotKey)?.label ?? "Erzeugungszähler"}"`;
  const gained = intakeSlots.filter((entry) => entry.available && !entry.counted);
  const lost = intakeSlots.filter((entry) => entry.counted && !entry.available);
  if (gained.length === 0 && lost.length === 0) return null;
  return {
    gained: gained.map((entry) => nameOf(entry.slotKey)),
    lost: lost.map((entry) => nameOf(entry.slotKey)),
  };
}

/** Die Sätze zu einer Änderung der Aufnahme: Hinweis, Frage nach dem Zeitpunkt, Erläuterung. */
export function intakeChangeTexts(change: IntakeChange): {
  readonly notice: string;
  readonly question: string;
  readonly note: string;
} {
  const subject = (names: readonly string[]) => {
    const [first, ...rest] = names;
    const joined =
      rest.length === 0 ? first! : `${names.slice(0, -1).join(", ")} und ${names.at(-1)!}`;
    return { joined, verb: names.length === 1 ? "zählt" : "zählen" };
  };
  const sentences: string[] = [];
  if (change.gained.length > 0) {
    const { joined, verb } = subject(change.gained);
    sentences.push(
      `${joined} ${verb} jetzt auch, was in die Anlagen hineingeht — etwa einen Speicher, der lädt.`,
    );
  }
  if (change.lost.length > 0) {
    const { joined, verb } = subject(change.lost);
    sentences.push(`${joined} ${verb} nicht mehr, was hineingeht.`);
  }
  const notice = `${sentences.join(" ")} Diese Fassung rechnet noch nach dem bisherigen Stand.`;
  const question =
    change.gained.length > 0 && change.lost.length === 0
      ? `${subject(change.gained).joined} ${subject(change.gained).verb} jetzt auch, was hineingeht — ab wann soll die Rechnung das berücksichtigen?`
      : change.lost.length > 0 && change.gained.length === 0
        ? `${subject(change.lost).joined} ${subject(change.lost).verb} nicht mehr, was hineingeht — ab wann?`
        : "Die Erzeugungszähler zählen die Aufnahme jetzt anders — ab wann soll die Rechnung das übernehmen?";
  const notes: string[] = [];
  if (change.gained.length > 0) {
    notes.push(
      "Ab diesem Zeitpunkt zieht die Rechnung die Aufnahme von der Erzeugung ab: Verbrauch = max(Netzbezug + Erzeugung − Aufnahme − Einspeisung, 0). Laden aus dem Netz ist dann kein Verbrauch.",
    );
  }
  if (change.lost.length > 0) {
    notes.push(
      change.gained.length > 0
        ? `Bei ${subject(change.lost).joined.replace(/^Der /u, "dem ")} zählt dann nur die Abgabe.`
        : "Ab diesem Zeitpunkt zählt dort nur die Abgabe.",
    );
  }
  return { notice, question, note: notes.join(" ") };
}

/**
 * Die Gegenrichtung eines Zählwerks — Bezug zu Einspeisung und umgekehrt. Die Aufnahme des
 * Erzeugungszählers ist immer die Gegenrichtung seiner Abgabe (#40); `null` für eine Rolle
 * ohne Richtung.
 */
export function oppositeRole(role: Role): Role | null {
  if (role === "energy_import_total") return "energy_export_total";
  if (role === "energy_export_total") return "energy_import_total";
  return null;
}

/** Ob der Zähler ein Zählwerk dieser Rolle führt. */
export function meterHasRole(meter: RegistersOf, role: Role): boolean {
  return meter.registers.some((register) => register.role === role);
}

/** Die Rollen, die der Eingang annimmt — `roles`, sonst die Vorgabe. */
function allowedRoles(input: TemplateInput): readonly Role[] {
  return input.roles.length > 0 ? input.roles : [input.role];
}

/**
 * Das Zählwerk, aus dem ein Eingang mit diesem Zähler zählt: die gewählte
 * Rolle, **wenn der Zähler sie führt und der Eingang sie annimmt**; sonst die
 * erste zulässige, die der Zähler führt; sonst die Vorgabe der Vorlage — dann
 * weist der Server ab, statt dass hier eine Rolle geraten wird.
 */
export function roleForInput(
  input: TemplateInput,
  meter: RegistersOf,
  chosen: Role | undefined,
): Role {
  const allowed = allowedRoles(input);
  if (chosen !== undefined && allowed.includes(chosen) && meterHasRole(meter, chosen)) {
    return chosen;
  }
  return allowed.find((role) => meterHasRole(meter, role)) ?? input.role;
}

/**
 * Die neue Zuordnung: der Steckplatz bekommt den Zähler (oder keinen), alle
 * anderen behalten ihren. Jeder Eingang des Steckplatzes zeigt auf die
 * Messstelle des Zählers — ein Zweirichtungszähler füllt Bezug und
 * Einspeisung zugleich.
 */
export function assignmentWith(
  template: Pick<Template, "inputs">,
  assignment: Assignment,
  slotKey: string,
  meter: (RegistersOf & { readonly meteringPointId: string }) | null,
  roles: Readonly<Record<string, Role>>,
): Assignment {
  const next: Assignment = { ...assignment };
  for (const input of template.inputs.filter((entry) => entry.slot === slotKey)) {
    /* Ohne Zähler, oder ein optionaler Eingang, den dieser Zähler nicht zählt:
       dann bleibt er leer und fällt aus dem Graphen (HM-171). */
    if (
      meter === null ||
      (!input.required && !allowedRoles(input).some((role) => meterHasRole(meter, role)))
    ) {
      delete next[input.key];
      continue;
    }
    next[input.key] = {
      meteringPointId: meter.meteringPointId,
      role: roleForInput(input, meter, roles[input.key]),
    };
  }
  /* **Die Aufnahme zählt die Gegenrichtung der Abgabe** (#40) — je Erzeugungszähler die seines
     Steckplatzes (#43). Ohne diese Zeile nähme sie die erste zulässige Rolle — oft dieselbe wie
     die Abgabe, und der Server wiese ab. */
  const intake = template.inputs.find(
    (entry) => isIntakeInput(entry.key) && entry.slot === slotKey,
  );
  const generation = template.inputs.find(
    (entry) => entry.slot === slotKey && !isIntakeInput(entry.key),
  );
  if (intake !== undefined) {
    const produced = generation === undefined ? undefined : next[generation.key];
    const opposite = produced === undefined ? null : oppositeRole(produced.role);
    /* Führt der neue Zähler keine Gegenrichtung, gibt es keine Aufnahme. Entscheiden tut der
       Server (#42) — er leitet sie aus dem Zähler ab; hier steht sie nur, damit die Skizze bis
       zur Antwort stimmt. */
    if (meter !== null && opposite !== null && meterHasRole(meter, opposite)) {
      next[intake.key] = { meteringPointId: meter.meteringPointId, role: opposite };
    } else {
      delete next[intake.key];
    }
  }
  return next;
}

/**
 * Die Steckplätze, die eine Layout-Änderung wegnimmt und in denen heute ein
 * Zähler steckt. Ein entfernter Verbraucher nimmt seinen Steckplatz mit, eine
 * kleinere Erzeugerzahl die hintersten der Familie — das ist
 * ein „Zuordnung aufheben" und verlangt dieselbe Bestätigung.
 *
 * Die Steckplätze einer Familie stehen in der expandierten Vorlage in ihrer
 * Reihenfolge; welcher wegfällt, wird daraus abgelesen und nicht aus dem
 * Schlüssel gelesen (der ist opak).
 */
export function droppedOccupiedSlots(
  slots: readonly Pick<Slot, "key" | "repeat">[],
  before: Pick<Layout, "consumers" | "generatorCount">,
  after: Pick<Layout, "consumers" | "generatorCount">,
  occupied: (slotKey: string) => boolean,
  slotOfConsumer: (consumerKey: string) => string | null,
): readonly string[] {
  const kept = new Set(
    after.consumers.flatMap((consumer) => (consumer.key === undefined ? [] : [consumer.key])),
  );
  const consumers = before.consumers.flatMap((consumer) => {
    if (consumer.key === undefined || kept.has(consumer.key)) return [];
    const slot = slotOfConsumer(consumer.key);
    return slot === null ? [] : [slot];
  });
  const tail = (repeat: Slot["repeat"], count: number) =>
    slots
      .filter((slot) => slot.repeat === repeat)
      .slice(count)
      .map((slot) => slot.key);
  const generators =
    after.generatorCount < before.generatorCount ? tail("per_generator", after.generatorCount) : [];
  return [...consumers, ...generators].filter(occupied);
}

/**
 * Wie der Editor „nicht gefunden" behandelt: **nie als „leeren".** Steht in der
 * Wahl ein Zähler, der unter den wählbaren nicht (mehr) vorkommt — etwa nach
 * einem Wechsel der Anlage, oder weil er inzwischen archiviert ist —, wird
 * nicht gespeichert. `assignmentWith(…, null)` hieße sonst, den Steckplatz
 * auszuräumen, den niemand ausräumen wollte.
 */
export function resolveChosenMeter<M extends { readonly id: string }>(
  meterId: string | null,
  usable: readonly M[],
): { readonly ok: true; readonly meter: M | null } | { readonly ok: false } {
  if (meterId === null) return { ok: true, meter: null };
  const meter = usable.find((candidate) => candidate.id === meterId);
  return meter === undefined ? { ok: false } : { ok: true, meter };
}
